/**
 * The client directory: search box, Add Client/Lead flow, and the Clients,
 * Leads and Void Clients tables.
 *
 * Extracted from the Client page so the Service and Support > Clients tab is the
 * same component rather than a copy that can drift out of step.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchableDropdown from './SearchableDropdown';
import Modal from './Modal';
import DuplicateClientModal from './DuplicateClientModal';
import MarketingOptOutBadge from './MarketingOptOutBadge';
import { SortableTh } from './SortableTh';
import { fetchClients, createClient, DuplicateClientError, type ClientRow, type ExistingClientRef } from '../api/clients';
import { fetchVerifiedUsers, type VerifiedUser } from '../api/users';
import { useInventory } from '../context/InventoryContext';
import { INDUSTRIES } from '../constants/industries';
import { US_STATES, regionForState } from '../constants/usRegions';
import { useTableSortState } from '../hooks/useTableSort';
import { sortRows } from '../utils/tableSort';
// Generic despite the name: Postgres DATE columns arrive as full ISO strings,
// and this renders them without the timezone shifting the day backwards.
import { formatTaskDate } from '../utils/formatTaskDate';

const ClientDirectory: React.FC = () => {
  const navigate = useNavigate();
  const { products: inventoryProducts } = useInventory();
  const [apiClients, setApiClients] = useState<ClientRow[]>([]);
  const [apiVoidClients, setApiVoidClients] = useState<ClientRow[]>([]);
  const [apiLeads, setApiLeads] = useState<ClientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [duplicateClient, setDuplicateClient] = useState<ExistingClientRef | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    clients: false,
    leads: false,
    void: true,
  });
  const toggleSection = (key: string) =>
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const [formType, setFormType] = useState<'client' | 'lead'>('client');
  const [formCompany, setFormCompany] = useState('');
  const [formPointOfContact, setFormPointOfContact] = useState('');
  const [formContactEmail, setFormContactEmail] = useState('');
  const [formContactPhone, setFormContactPhone] = useState('');
  const [formProduct, setFormProduct] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formStartDate, setFormStartDate] = useState('');
  const [formBillingAddress, setFormBillingAddress] = useState('');
  const [formSiteLocation, setFormSiteLocation] = useState('');
  const [formStateCode, setFormStateCode] = useState('');
  const [formSource, setFormSource] = useState('');
  const [formIndustry, setFormIndustry] = useState('');
  const [formFirstContact, setFormFirstContact] = useState('');
  const [formEmployeeName, setFormEmployeeName] = useState('');
  const [formSelectedUserId, setFormSelectedUserId] = useState<number | null>(null);
  const [verifiedUsers, setVerifiedUsers] = useState<VerifiedUser[]>([]);
  const [clientSearchQuery, setClientSearchQuery] = useState('');

  const loadLists = async () => {
    try {
      const [clientsRes, voidClientsRes, leadsRes] = await Promise.all([
        fetchClients('client', undefined, 'Active'),
        fetchClients('client', undefined, 'Void'),
        fetchClients('lead'),
      ]);
      setApiClients(clientsRes);
      setApiVoidClients(voidClientsRes);
      setApiLeads(leadsRes);
    } catch {
      setApiClients([]);
      setApiVoidClients([]);
      setApiLeads([]);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const users = await fetchVerifiedUsers();
        if (!cancelled) setVerifiedUsers(users);
      } catch {
        if (!cancelled) setVerifiedUsers([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadLists();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const selectedRegion = useMemo(() => regionForState(formStateCode), [formStateCode]);

  const searchLower = clientSearchQuery.trim().toLowerCase();
  const filteredClients = useMemo(() => {
    if (!searchLower) return apiClients;
    return apiClients.filter((c) =>
      [c.company, c.employee_name, c.point_of_contact, c.product, c.job_code].some((v) =>
        (v ?? '').toLowerCase().includes(searchLower)
      )
    );
  }, [apiClients, searchLower]);
  const filteredLeads = useMemo(() => {
    if (!searchLower) return apiLeads;
    return apiLeads.filter((l) =>
      [l.company, l.point_of_contact, l.contact_email, l.contact_phone, l.source, l.employee_name].some((v) => (v ?? '').toLowerCase().includes(searchLower))
    );
  }, [apiLeads, searchLower]);
  const filteredVoidClients = useMemo(() => {
    if (!searchLower) return apiVoidClients;
    return apiVoidClients.filter((c) =>
      [c.company, c.employee_name, c.point_of_contact, c.product, c.void_reason, c.job_code].some((v) =>
        (v ?? '').toLowerCase().includes(searchLower)
      )
    );
  }, [apiVoidClients, searchLower]);

  const clientSort = useTableSortState();
  const leadSort = useTableSortState();
  const voidSort = useTableSortState();
  const sortedClients = useMemo(
    () =>
      sortRows(filteredClients, clientSort.sortKey, clientSort.sortDir, (c, key) => {
        switch (key) {
          case 'company':
            return c.company ?? '';
          case 'employee':
            return c.employee_name ?? '';
          case 'poc':
            return c.point_of_contact ?? '';
          case 'product':
            return c.product ?? '';
          default:
            return '';
        }
      }),
    [filteredClients, clientSort.sortKey, clientSort.sortDir]
  );
  const sortedLeads = useMemo(
    () =>
      sortRows(filteredLeads, leadSort.sortKey, leadSort.sortDir, (l, key) => {
        switch (key) {
          case 'company':
            return l.company ?? '';
          case 'employee':
            return l.employee_name ?? '';
          case 'poc':
            return l.point_of_contact ?? '';
          case 'contact':
            return [l.contact_email, l.contact_phone].filter(Boolean).join(' | ');
          case 'source':
            return l.source ?? '';
          // ISO dates sort correctly as text; blanks sort last either way.
          case 'first_contact':
            return l.first_contact ?? '';
          default:
            return '';
        }
      }),
    [filteredLeads, leadSort.sortKey, leadSort.sortDir]
  );
  const sortedVoidClients = useMemo(
    () =>
      sortRows(filteredVoidClients, voidSort.sortKey, voidSort.sortDir, (c, key) => {
        switch (key) {
          case 'company':
            return c.company ?? '';
          case 'employee':
            return c.employee_name ?? '';
          case 'poc':
            return c.point_of_contact ?? '';
          case 'reason':
            return c.void_reason ?? '';
          default:
            return '';
        }
      }),
    [filteredVoidClients, voidSort.sortKey, voidSort.sortDir]
  );

  const inventoryProductNames = useMemo(
    () => Array.from(new Set(inventoryProducts.map((p) => p.name))),
    [inventoryProducts]
  );

  const handleClientClick = (clientId: number) => {
    navigate(`/client/${clientId}`);
  };

  const handleLeadClick = (leadId: number) => {
    navigate(`/lead/${leadId}`);
  };

  /**
   * Opens the add form for one kind of record. The type comes from whichever
   * button was pressed rather than a picker inside the form, so "Add Lead" and
   * "Add Client" each land on the fields that apply to them.
   */
  const openAddModal = (type: 'client' | 'lead') => {
    setFormType(type);
    setFormCompany('');
    setFormPointOfContact('');
    setFormContactEmail('');
    setFormContactPhone('');
    setFormProduct('');
    setFormNotes('');
    setFormStartDate('');
    setFormBillingAddress('');
    setFormSiteLocation('');
    setFormStateCode('');
    setFormSource('');
    setFormIndustry('');
    setFormFirstContact('');
    setFormEmployeeName('');
    setFormSelectedUserId(null);
    setSubmitError('');
    setDuplicateClient(null);
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
    setSubmitError('');
    setDuplicateClient(null);
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    const company = formCompany.trim();
    const pointOfContact = formPointOfContact.trim();
    if (!company) {
      setSubmitError('Company is required.');
      return;
    }
    if (!pointOfContact) {
      setSubmitError('Point of contact is required.');
      return;
    }
    if (formType === 'lead' && !formSource.trim()) {
      setSubmitError('Source is required for leads.');
      return;
    }
    setSubmitting(true);
    try {
      const created = await createClient({
        company,
        type: formType,
        point_of_contact: pointOfContact,
        contact_email: formContactEmail.trim() || undefined,
        contact_phone: formContactPhone.trim() || undefined,
        product: formProduct.trim() || undefined,
        notes: formNotes.trim() || undefined,
        start_date: formStartDate.trim() || undefined,
        billing_address: formBillingAddress.trim() || undefined,
        site_location: formSiteLocation.trim() || undefined,
        state_code: formType === 'client' ? formStateCode || undefined : undefined,
        source: formType === 'lead' ? formSource.trim() || undefined : undefined,
        industry: formIndustry.trim() || undefined,
        first_contact: formFirstContact.trim() || undefined,
        // Leads carry an assigned employee too — the person doing the outreach.
        ...(formSelectedUserId != null
          ? { user_id: formSelectedUserId }
          : { employee_name: formEmployeeName.trim() || undefined }),
      });
      if (formType === 'client') {
        setApiClients((prev) => [...prev, created]);
      } else {
        setApiLeads((prev) => [...prev, created]);
      }
      closeAddModal();
      await loadLists();
      navigate(formType === 'client' ? `/client/${created.id}` : `/lead/${created.id}`);
    } catch (err) {
      if (err instanceof DuplicateClientError) {
        // The form stays open behind this so the name can be corrected.
        setDuplicateClient(err.existing);
        setSubmitError('');
      } else {
        setSubmitError(err instanceof Error ? err.message : 'Failed to add. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="page-toolbar">
        <input
          type="text"
          className="page-toolbar-search"
          placeholder="Search clients and leads..."
          value={clientSearchQuery}
          onChange={(e) => setClientSearchQuery(e.target.value)}
          aria-label="Search clients and leads"
        />
      </div>

      {loading ? (
        <p className="page-subtitle">Loading clients...</p>
      ) : (
        <div className="clients-tables-container">
          {/* Leads sit above clients: the top of the funnel is what gets worked daily. */}
          <div className="leads-section">
            <div className="collapsible-header" onClick={() => toggleSection('leads')}>
              <h3 className="leads-title">Leads</h3>
              <span className={`collapse-arrow${!collapsedSections.leads ? ' collapse-arrow--open' : ''}`}>▶</span>
              {/* Inside a header that toggles the section, so the click stops here. */}
              <button
                type="button"
                className="clients-section-add-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  openAddModal('lead');
                }}
              >
                +&nbsp; Add Lead
              </button>
            </div>
            {!collapsedSections.leads && <div className="collapsible-table-wrapper"><table className="leads-table">
              <thead>
                <tr>
                  <SortableTh label="Company Name" columnKey="company" sortKey={leadSort.sortKey} sortDir={leadSort.sortDir} onSort={leadSort.toggleSort} />
                  <SortableTh label="Employee" columnKey="employee" sortKey={leadSort.sortKey} sortDir={leadSort.sortDir} onSort={leadSort.toggleSort} />
                  <SortableTh label="Point of Contact" columnKey="poc" sortKey={leadSort.sortKey} sortDir={leadSort.sortDir} onSort={leadSort.toggleSort} />
                  <SortableTh label="Contact Information" columnKey="contact" sortKey={leadSort.sortKey} sortDir={leadSort.sortDir} onSort={leadSort.toggleSort} />
                  <SortableTh label="First Contact" columnKey="first_contact" sortKey={leadSort.sortKey} sortDir={leadSort.sortDir} onSort={leadSort.toggleSort} />
                  <SortableTh label="Source" columnKey="source" sortKey={leadSort.sortKey} sortDir={leadSort.sortDir} onSort={leadSort.toggleSort} />
                </tr>
              </thead>
              <tbody>
                {sortedLeads.map((lead) => (
                  <tr
                    key={lead.id}
                    onClick={() => handleLeadClick(lead.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td data-label="Company">{lead.company}</td>
                    <td data-label="Employee">{lead.employee_name || 'unassigned'}</td>
                    <td data-label="Point of Contact">{lead.point_of_contact}</td>
                    <td data-label="Contact">
                      {[lead.contact_email, lead.contact_phone].filter(Boolean).join(' | ') || '—'}
                      <MarketingOptOutBadge client={lead} compact />
                    </td>
                    <td data-label="First Contact">{formatTaskDate(lead.first_contact)}</td>
                    <td data-label="Source">{lead.source ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>}
            {filteredLeads.length === 0 && !collapsedSections.leads && (
              <p className="page-subtitle">{apiLeads.length === 0 ? 'No leads yet.' : 'No leads match your search.'}</p>
            )}
          </div>

          <div className="clients-section">
            <div className="collapsible-header" onClick={() => toggleSection('clients')}>
              <h3 className="clients-title">Clients</h3>
              <span className={`collapse-arrow${!collapsedSections.clients ? ' collapse-arrow--open' : ''}`}>▶</span>
              <button
                type="button"
                className="clients-section-add-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  openAddModal('client');
                }}
              >
                +&nbsp; Add Client
              </button>
            </div>
            {!collapsedSections.clients && <div className="collapsible-table-wrapper"><table className="clients-table">
              <thead>
                <tr>
                  <SortableTh label="Company" columnKey="company" sortKey={clientSort.sortKey} sortDir={clientSort.sortDir} onSort={clientSort.toggleSort} />
                  <SortableTh label="Employee" columnKey="employee" sortKey={clientSort.sortKey} sortDir={clientSort.sortDir} onSort={clientSort.toggleSort} />
                  <SortableTh label="Point of Contact" columnKey="poc" sortKey={clientSort.sortKey} sortDir={clientSort.sortDir} onSort={clientSort.toggleSort} />
                  <SortableTh label="Product" columnKey="product" sortKey={clientSort.sortKey} sortDir={clientSort.sortDir} onSort={clientSort.toggleSort} />
                </tr>
              </thead>
              <tbody>
                {sortedClients.map((client) => (
                  <tr
                    key={client.id}
                    onClick={() => handleClientClick(client.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td data-label="Company">{client.company}</td>
                    <td data-label="Employee">{client.employee_name || 'unassigned'}</td>
                    <td data-label="Point of Contact">
                      {client.point_of_contact}
                      <MarketingOptOutBadge client={client} compact />
                    </td>
                    <td data-label="Product">{client.product ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>}
            {filteredClients.length === 0 && !collapsedSections.clients && (
              <p className="page-subtitle">{apiClients.length === 0 ? 'No clients yet.' : 'No clients match your search.'}</p>
            )}
          </div>

          <div className="clients-section">
            <div className="collapsible-header" onClick={() => toggleSection('void')}>
              <h3 className="clients-title">Void Clients</h3>
              <span className={`collapse-arrow${!collapsedSections.void ? ' collapse-arrow--open' : ''}`}>▶</span>
            </div>
            {!collapsedSections.void && <div className="collapsible-table-wrapper"><table className="clients-table">
              <thead>
                <tr>
                  <SortableTh label="Company" columnKey="company" sortKey={voidSort.sortKey} sortDir={voidSort.sortDir} onSort={voidSort.toggleSort} />
                  <SortableTh label="Employee" columnKey="employee" sortKey={voidSort.sortKey} sortDir={voidSort.sortDir} onSort={voidSort.toggleSort} />
                  <SortableTh label="Point of Contact" columnKey="poc" sortKey={voidSort.sortKey} sortDir={voidSort.sortDir} onSort={voidSort.toggleSort} />
                  <SortableTh label="Void Reason" columnKey="reason" sortKey={voidSort.sortKey} sortDir={voidSort.sortDir} onSort={voidSort.toggleSort} />
                </tr>
              </thead>
              <tbody>
                {sortedVoidClients.map((client) => (
                  <tr
                    key={client.id}
                    onClick={() => handleClientClick(client.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td data-label="Company">{client.company}</td>
                    <td data-label="Employee">{client.employee_name || 'unassigned'}</td>
                    <td data-label="Point of Contact">{client.point_of_contact}</td>
                    <td data-label="Void Reason">{client.void_reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>}
            {filteredVoidClients.length === 0 && !collapsedSections.void && (
              <p className="page-subtitle">{apiVoidClients.length === 0 ? 'No void clients.' : 'No void clients match your search.'}</p>
            )}
          </div>
        </div>
      )}

      <Modal
        isOpen={addModalOpen}
        onClose={closeAddModal}
        title={formType === 'lead' ? 'Add Lead' : 'Add Client'}
        wide
      >
        <form onSubmit={handleAddSubmit}>
          <div className="modal-body">
            {/* ── Client / Lead info ── */}
            <div className="form-group">
              <label htmlFor="add-company" className="form-label">Company *</label>
              <input
                id="add-company" type="text" className="form-input"
                value={formCompany} onChange={(e) => setFormCompany(e.target.value)}
                required placeholder="Company name"
              />
            </div>

            {/* Both kinds of record have an owner; for a lead it is whoever is
                doing the outreach. */}
            <div className="form-group">
              <label htmlFor="add-employee" className="form-label">Assigned Employee</label>
              <SearchableDropdown
                options={verifiedUsers.map((u) => u.username)}
                value={formEmployeeName}
                onChange={(display) => {
                  setFormEmployeeName(display);
                  const u = verifiedUsers.find((x) => x.username === display);
                  setFormSelectedUserId(u ? u.id : null);
                }}
                placeholder="Select employee..."
              />
              {formType === 'lead' && (
                <p className="form-hint">Who is in charge of reaching out to this lead.</p>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="add-first-contact" className="form-label">First Contact</label>
              <input
                id="add-first-contact" type="date" className="form-input"
                value={formFirstContact} onChange={(e) => setFormFirstContact(e.target.value)}
              />
              <p className="form-hint">Date of the first outreach. Leave blank if nobody has made contact yet.</p>
            </div>

            {formType === 'client' && (
              <>
                <div className="form-group">
                  <label htmlFor="add-start-date" className="form-label">Start Date</label>
                  <input
                    id="add-start-date" type="date" className="form-input"
                    value={formStartDate} onChange={(e) => setFormStartDate(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="add-industry" className="form-label">Industry</label>
                  <SearchableDropdown
                    options={INDUSTRIES}
                    value={formIndustry}
                    onChange={setFormIndustry}
                    placeholder="Type to search or enter industry..."
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="add-product" className="form-label">Product</label>
                  <SearchableDropdown
                    options={inventoryProductNames}
                    value={formProduct}
                    onChange={setFormProduct}
                    placeholder="Select product..."
                  />
                </div>
              </>
            )}

            {formType === 'lead' && (
              <div className="form-group">
                <label htmlFor="add-source" className="form-label">Source *</label>
                <input
                  id="add-source" type="text" className="form-input"
                  value={formSource} onChange={(e) => setFormSource(e.target.value)}
                  required={formType === 'lead'} placeholder="e.g. Website Inquiry, Event"
                />
              </div>
            )}

            <hr style={{ margin: '1.25rem 0', borderColor: '#e5e7eb' }} />

            {/* ── Point of Contact ── */}
            <p className="client-info-subsection-title">Point of Contact</p>

            <div className="form-group">
              <label htmlFor="add-poc" className="form-label">Name *</label>
              <input
                id="add-poc" type="text" className="form-input"
                value={formPointOfContact} onChange={(e) => setFormPointOfContact(e.target.value)}
                required placeholder="Full name"
              />
            </div>
            <div className="form-group">
              <label htmlFor="add-email" className="form-label">Email</label>
              <input
                id="add-email" type="email" className="form-input"
                value={formContactEmail} onChange={(e) => setFormContactEmail(e.target.value)}
                placeholder="contact@company.com"
              />
            </div>
            <div className="form-group">
              <label htmlFor="add-phone" className="form-label">Phone</label>
              <input
                id="add-phone" type="tel" className="form-input"
                value={formContactPhone} onChange={(e) => setFormContactPhone(e.target.value)}
                placeholder="+1 (555) 000-0000"
              />
            </div>

            {formType === 'client' && (
              <>
                <div className="form-group">
                  <label htmlFor="add-billing" className="form-label">Billing Address</label>
                  <textarea
                    id="add-billing" className="form-textarea"
                    value={formBillingAddress} onChange={(e) => setFormBillingAddress(e.target.value)}
                    rows={2} placeholder="Street, City, State, ZIP"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="add-site" className="form-label">Site Location</label>
                  <textarea
                    id="add-site" className="form-textarea"
                    value={formSiteLocation} onChange={(e) => setFormSiteLocation(e.target.value)}
                    rows={2} placeholder="Installation or service address"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="add-state" className="form-label">State</label>
                  <select
                    id="add-state"
                    className="form-input"
                    value={formStateCode}
                    onChange={(e) => setFormStateCode(e.target.value)}
                  >
                    <option value="">Select a state…</option>
                    {US_STATES.map((s) => (
                      <option key={s.code} value={s.code}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                  <p className="form-hint">
                    {selectedRegion
                      ? `${selectedRegion.label} region — job code will start with ${selectedRegion.letter}.`
                      : 'Sets the region letter of this client’s Job Code / Deployment ID.'}
                  </p>
                </div>
              </>
            )}

            <hr style={{ margin: '1.25rem 0', borderColor: '#e5e7eb' }} />

              <div className="form-group">
                <label htmlFor="add-notes" className="form-label">Notes</label>
                <textarea
                  id="add-notes"
                  className="form-textarea"
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  rows={3}
                  placeholder="Notes..."
                />
              </div>

            {submitError && (
              <p className="add-client-error" role="alert">{submitError}</p>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={closeAddModal}>
              Cancel
            </button>
            <button type="submit" className="update-button" disabled={submitting}>
              {submitting ? 'Adding…' : 'Add'}
            </button>
          </div>
        </form>
      </Modal>

      <DuplicateClientModal existing={duplicateClient} onClose={() => setDuplicateClient(null)} />
    </>
  );
};

export default ClientDirectory;
